import { z } from "zod";

/**
 * List endpoints all take the same query and give the same page back, so the admin's data table
 * works for every one of them. The query schema is also the list page's URL (`validateSearch`):
 * the search text, filters, sort and page survive a refresh and can be shared as a link.
 *
 * Every field forgives a bad value (`?page=abc` is page 1), because a URL is typed by hand too.
 *
 * ```ts
 * export const ProductListInput = listInput({
 *   sort: ["name", "createdAt"],
 *   defaultSort: "name",
 *   filters: { active: z.enum(["yes", "no"]).optional().catch(undefined) },
 * });
 * export const ProductList = listPage(Product);
 * ```
 */

export const PAGE_SIZES = [10, 25, 50, 100] as const;
export const DEFAULT_PAGE_SIZE = 25;

export const SortOrder = z.enum(["asc", "desc"]);
export type SortOrder = z.infer<typeof SortOrder>;

export function listInput<
	const Sort extends readonly [string, ...string[]],
	Filters extends z.ZodRawShape = Record<never, never>,
>(options: {
	readonly sort: Sort;
	readonly defaultSort: Sort[number];
	readonly defaultOrder?: SortOrder;
	readonly filters?: Filters;
}) {
	const defaultOrder = options.defaultOrder ?? "asc";
	return z.object({
		/** Free text; each list decides which fields it searches. */
		search: z.string().trim().max(100).optional().catch(undefined),
		sort: z.enum(options.sort).default(options.defaultSort).catch(options.defaultSort),
		order: SortOrder.default(defaultOrder).catch(defaultOrder),
		page: z.coerce.number().int().min(1).max(100_000).default(1).catch(1),
		pageSize: z.coerce
			.number()
			.pipe(z.literal(PAGE_SIZES))
			.default(DEFAULT_PAGE_SIZE)
			.catch(DEFAULT_PAGE_SIZE),
		...(options.filters ?? ({} as Filters)),
	});
}

/** One page of a list: its rows, and the total that matches (for "26–50 of 112"). */
export const listPage = <Item extends z.ZodType>(item: Item) =>
	z.object({
		items: z.array(item),
		total: z.number().int(),
		page: z.number().int(),
		pageSize: z.number().int(),
	});
